import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import GoogleMap from '../../components/GoogleMap';

const HalleHome = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-turquoise-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden" data-testid="halle-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-turquoise-400 to-turquoise-300 opacity-10"></div>
        <div className="container mx-auto text-center relative z-10">
          <div className="mb-4">
            <span className="bg-turquoise-400 text-white px-6 py-3 rounded-full text-lg font-bold inline-block">
              +++ JETZT NEU !!! +++
            </span>
          </div>
          <div className="mb-8">
            <img 
              src="https://beratung.grossstadtherzen.de/logo-transparent.png" 
              alt="Großstadtherzen Logo" 
              className="h-80 w-auto mx-auto mb-6"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Ambulante Pflege Großstadtherzen®
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8">
            Standort <span className="text-turquoise-400 font-bold">Halle (Saale)</span>
          </p>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Bei uns werden sie „Groß" geschrieben
          </p>
        </div>
      </section>

      {/* Announcement Banner */}
      <section className="py-8 px-4 bg-gradient-to-r from-turquoise-400 to-green-600 text-white">
        <div className="container mx-auto text-center">
          <p className="text-2xl md:text-3xl font-bold">
            Wir sind auch in Halle (Saale) vertreten!
          </p>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8">
            Herzlich Willkommen
          </h2>
          <div className="prose prose-lg mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Wir sind ein kompetentes Team aus qualifizierten Pflegekräften und bieten Ihnen ein umfangreiches Paket an pflegerischer Unterstützung an, um Ihnen die bestmögliche individuelle Pflege und Beratung zu versichern.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Ob Versorgung nach Pflegegraden, Klienten im Rahmen der Hauswirtschaft oder Betreuung sowie Klienten die einer Behandlungspflege bedürfen – wir helfen Ihnen gern.
            </p>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1543333995-a78aea2eee50?q=85&w=800" 
                alt="Pflege in Halle" 
                className="rounded-3xl shadow-2xl"
                data-testid="halle-care-image"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Professionelle Pflege in Halle</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Mit unserem neuen Standort in Halle (Saale) erweitern wir unser Angebot und bringen unsere bewährte Pflegephilosophie auch zu Ihnen.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Unser erfahrenes Team steht Ihnen mit Herz und Kompetenz zur Seite.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Link */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-gradient-to-br from-turquoise-50 to-turquoise-100 rounded-3xl shadow-xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Unsere Firmenphilosophie</h2>
            <p className="text-lg text-gray-700 mb-8">
              Erfahren Sie mehr über unsere Werte und unser Leitbild
            </p>
            <Link 
              to="/halle/ueber-uns"
              className="inline-block bg-turquoise-400 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-turquoise-500 transition-all shadow-lg"
            >
              Mehr erfahren →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Button */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-2xl">
          <Link 
            to="/angebot"
            className="block bg-gradient-to-r from-turquoise-400 to-green-600 text-white p-8 rounded-2xl text-center hover:from-turquoise-500 hover:to-green-700 transition-all shadow-lg hover:shadow-2xl transform hover:-translate-y-1"
            data-testid="halle-angebot-link"
          >
            <h3 className="text-2xl font-bold mb-2">UNVERBINDLICHES ANGEBOT</h3>
            <p className="text-turquoise-100">Fordern Sie jetzt ein kostenloses Angebot an</p>
          </Link>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Kontakt Halle (Saale)</h2>
          <div className="bg-gradient-to-br from-turquoise-50 to-turquoise-100 rounded-3xl shadow-xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Adresse
                </h3>
                <p className="text-gray-700">
                  Ambulante Pflege Großstadtherzen® Halle UG<br />
                  Merseburger Str. 19<br />
                  06112 Halle (Saale)
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Kontakt
                </h3>
                <p className="text-gray-700">
                  <a href="tel:034568669465" className="hover:text-turquoise-400 transition-colors font-semibold">Tel: 0345-68669465</a><br />
                  <span className="text-gray-600">Fax: 0345-68669469</span><br />
                  <a href="mailto:info@grossstadtherzen-halle.de" className="hover:text-turquoise-400 transition-colors">info@grossstadtherzen-halle.de</a>
                </p>
              </div>
            </div>
            <GoogleMap location="halle" className="mt-6" />
          </div>
        </div>
      </section>

      {/* Quality Message */}
      <section className="py-16 px-4 bg-gradient-to-br from-turquoise-400 to-green-600 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Auch Ihnen steht eine qualitativ hochwertige Pflege zu.
          </h2>
          <p className="text-xl mb-4">Nutzen Sie diese.</p>
          <p className="text-2xl font-bold">
            Qualitativ hochwertige Pflege darf nicht am Geld scheitern.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HalleHome;